// src/pages/StaffDetailPage.jsx
import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Icon } from "@iconify/react/dist/iconify.js";
import { staffApi } from "../Apis/Api";
import { formatPhoneNumber, formatDate } from "../utils/formatters";
import LoadingComponent from "../components/ui/LoadingComponent";
import { handleApiResponse, handleCustomResponse } from "../utils/response";
import Breadcrumb from "../components/Breadcrumb";

const StaffDetailpage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const staffId = location.state?.staffData?.id;
  const [staffData, setStaffData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Get initials from name
  const getInitials = (name) => {
    if (!name) return "SM";
    const names = name.trim().split(" ");
    if (names.length >= 2) {
      return (names[0][0] + names[names.length - 1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  // Generate color based on name
  const getAvatarColor = (name) => {
    if (!name) return "#487BFF";
    const colors = [
      "#487BFF",
      "#FF6B6B",
      "#4ECDC4",
      "#45B7D1",
      "#FFA07A",
      "#98D8C8",
      "#F7DC6F",
      "#BB8FCE",
      "#85C1E2",
      "#F8B739",
    ];
    const charCode = name.charCodeAt(0);
    return colors[charCode % colors.length];
  };

  useEffect(() => {
    const fetchStaffDetails = async () => {
      if (!staffId) {
        setError("Invalid staff ID!");
        setLoading(false);
        return;
      }

      setLoading(true);
      setError("");

      try {
        const response = await staffApi.getStaffById(staffId);
        const staff = response.data;
        if (!staff) {
          throw new Error("No staff data found");
        }

        const formattedStaffData = {
          ...staff,
          name: staff.staff_name || "N/A",
          position: staff.role_name || "-",
          status:
            staff.is_active !== undefined
              ? staff.is_active
                ? "Active"
                : "Inactive"
              : "-",
          email: staff.staff_email || "-",
          phone_no: formatPhoneNumber(staff.staff_phone),
          created_datetime: formatDate(staff.created_datetime),
          updated_datetime: formatDate(staff.updated_datetime),
          created_by: staff.created_by_name || "-",
          updated_by: staff.updated_by_name || "-",
        };

        setStaffData(formattedStaffData);
        handleApiResponse(response, "success", "GET");
      } catch (err) {
        setError(err?.message || "Error fetching staff details.");
        handleApiResponse(err, "Err", "GET");
      } finally {
        setLoading(false);
      }
    };

    fetchStaffDetails();
  }, [staffId]);

  const navigateBack = () => {
    navigate("/staff");
  };

  const navigateEdit = () => {
    if (!staffData) {
      handleCustomResponse("error", "No staff data available to edit");
      return;
    }
    navigate("/staff-edit", { state: { staffData } });
  };

  return (
    <>
      <Breadcrumb title="View Staff" />

      <div className="p-4 p-md-3 p-sm-2">
        {error && (
          <div
            className="alert alert-danger alert-dismissible fade show mb-4"
            role="alert"
            style={{ borderRadius: "8px" }}
          >
            <Icon
              icon="material-symbols:error"
              className="me-3"
              style={{ fontSize: "20px" }}
            />
            {error}
            <button
              type="button"
              className="btn-close"
              onClick={() => setError("")}
            ></button>
          </div>
        )}

        {loading ? (
          <div className="text-center py-5">
            <LoadingComponent />
            <p className="mt-3 text-muted"></p>
          </div>
        ) : staffData ? (
          <>
            {/* Hero Staff Card */}
            <div
              className="card shadow-sm mb-4"
              style={{ border: "none", borderRadius: "8px" }}
            >
              <div className="card-body p-4">
                {/* Back Button */}
                <div className="d-flex justify-content-end gap-2">
                  <button
                    className="btn btn-sm btn-primary-600 d-flex align-items-center"
                    onClick={navigateBack}
                    style={{
                      borderRadius: "8px",
                      padding: "6px 12px",
                      transition: "all 0.3s ease",
                    }}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.transform = "scale(1.05)")
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.transform = "scale(1)")
                    }
                  >
                    <Icon
                      icon="ep:arrow-left"
                      className="me-2"
                      style={{ fontSize: "16px" }}
                    />
                    Back To Staff
                  </button>
                </div>

                <div className="text-center mb-4">
                  {/* Large Avatar - Centered */}
                  <div className="d-flex justify-content-center mb-3">
                    <div
                      style={{
                        width: "120px",
                        height: "120px",
                        borderRadius: "50%",
                        backgroundColor: getAvatarColor(staffData.name),
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "40px",
                        fontWeight: "600",
                        color: "white",
                        border: "3px solid #fff",
                        boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
                      }}
                    >
                      {getInitials(staffData.name)}
                    </div>
                  </div>

                  {/* Staff Name & Position */}
                  <h4
                    className="text-primary-600 fw-bold mb-2"
                    style={{ fontWeight: "600" }}
                  >
                    {staffData.name || "N/A"}
                  </h4>
                  <p className="text-muted mb-3">{staffData.position}</p>

                  <div className="d-flex justify-content-center">
                    <span
                      className={`px-3 py-1 fw-medium text-sm d-inline-flex align-items-center ${
                        staffData.status === "Active"
                          ? "bg-success-focus text-success-600 border border-success-main"
                          : "bg-danger-focus text-danger-600 border border-danger-main"
                      }`}
                      style={{ borderRadius: "8px" }}
                    >
                      <Icon
                        icon={
                          staffData.status === "Active"
                            ? "material-symbols:check-circle"
                            : "material-symbols:cancel"
                        }
                        className="me-2"
                        style={{ fontSize: "16px" }}
                      />
                      {staffData.status === "Active"
                        ? "Active Member"
                        : "Inactive Member"}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Information Grid */}
            <div className="row g-3">
              {/* Contact Information */}
              <div className="col-lg-6 col-md-6">
                <div
                  className="card shadow-sm h-100"
                  style={{ border: "none", borderRadius: "8px" }}
                >
                  <div className="card-body p-3 p-sm-2">
                    <div className="d-flex align-items-center mb-3">
                      <div className="p-2 bg-primary-focus rounded-circle me-3">
                        <Icon
                          icon="material-symbols:contact-mail"
                          className="text-primary-600"
                          style={{ fontSize: "20px" }}
                        />
                      </div>
                      <h6 className="fw-medium text-primary-600 mb-0">
                        Contact Details
                      </h6>
                    </div>
                    <div className="row g-2">
                      {staffData.email && staffData.email !== "-" && (
                        <div className="col-12">
                          <div className="p-2">
                            <div className="d-flex align-items-start">
                              <Icon
                                icon="material-symbols:mail-outline"
                                className="text-success-600 me-3 mt-1"
                                style={{ fontSize: "18px", minWidth: "18px" }}
                              />
                              <div className="flex-grow-1">
                                <label className="fw-medium text-primary-600 d-block">
                                  Email Address
                                </label>
                                <p
                                  className="mb-0 text-break"
                                  style={{
                                    wordBreak: "break-word",
                                    overflowWrap: "break-word",
                                  }}
                                >
                                  {staffData.email}
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                      {staffData.phone_no && staffData.phone_no !== "-" && (
                        <div className="col-12">
                          <div className="p-2">
                            <div className="d-flex align-items-start">
                              <Icon
                                icon="material-symbols:call"
                                className="text-success-600 me-3 mt-1"
                                style={{ fontSize: "18px", minWidth: "18px" }}
                              />
                              <div className="flex-grow-1">
                                <label className="fw-medium text-primary-600 d-block">
                                  Phone Number
                                </label>
                                <p className="mb-0">{staffData.phone_no}</p>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Role Information */}
              <div className="col-lg-6 col-md-6">
                <div
                  className="card shadow-sm h-100"
                  style={{ border: "none", borderRadius: "8px" }}
                >
                  <div className="card-body p-3 p-sm-2 d-flex flex-column">
                    <div className="d-flex align-items-center mb-3">
                      <div className="p-2 bg-warning-focus rounded-circle me-3">
                        <Icon
                          icon="material-symbols:badge"
                          className="text-warning-600"
                          style={{ fontSize: "20px" }}
                        />
                      </div>
                      <h6 className="fw-medium text-primary-600 mb-0">
                        Role & Position
                      </h6>
                    </div>
                    <div className="text-center flex-grow-1 d-flex flex-column justify-content-center">
                      <div className="p-3">
                        <Icon
                          icon="material-symbols:work"
                          className="text-warning-600 mb-2"
                          style={{ fontSize: "28px" }}
                        />
                        <h6 className="fw-medium text-warning-600 mb-1">
                          {staffData.position}
                        </h6>
                        <p className="mb-0 small">Current Position</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Account Information */}
              <div className="col-lg-12 col-md-12">
                <div
                  className="card shadow-sm h-100"
                  style={{ border: "none", borderRadius: "8px" }}
                >
                  <div className="card-body p-3 p-sm-2">
                    <div className="d-flex align-items-center mb-3">
                      <div className="p-2 bg-secondary-focus rounded-circle me-3">
                        <Icon
                          icon="material-symbols:account-circle"
                          className="text-secondary-600"
                          style={{ fontSize: "20px" }}
                        />
                      </div>
                      <h6 className="fw-medium text-primary-600 mb-0">
                        Account Details
                      </h6>
                    </div>
                    <div className="row g-2">
                      {staffData.created_by && staffData.created_by !== "-" && (
                        <div className="col-lg-6 col-md-6">
                          <div className="p-2">
                            <div className="d-flex align-items-start">
                              <Icon
                                icon="material-symbols:person-add"
                                className="text-primary-600 me-3 mt-1"
                                style={{ fontSize: "18px", minWidth: "18px" }}
                              />
                              <div className="flex-grow-1">
                                <label className="fw-medium text-primary-600 d-block">
                                  Created By
                                </label>
                                <p className="mb-0">{staffData.created_by}</p>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                      {staffData.created_datetime &&
                        staffData.created_datetime !== "-" && (
                          <div className="col-lg-6 col-md-6">
                            <div className="p-2">
                              <div className="d-flex align-items-start">
                                <Icon
                                  icon="material-symbols:calendar-today"
                                  className="text-success-600 me-3 mt-1"
                                  style={{ fontSize: "18px", minWidth: "18px" }}
                                />
                                <div className="flex-grow-1">
                                  <label className="fw-medium text-primary-600 d-block">
                                    Account Created
                                  </label>
                                  <p className="mb-0">
                                    {staffData.created_datetime}
                                  </p>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                      {staffData.updated_by && staffData.updated_by !== "-" && (
                        <div className="col-lg-6 col-md-6">
                          <div className="p-2">
                            <div className="d-flex align-items-start">
                              <Icon
                                icon="material-symbols:person-edit"
                                className="text-warning-600 me-3 mt-1"
                                style={{ fontSize: "18px", minWidth: "18px" }}
                              />
                              <div className="flex-grow-1">
                                <label className="fw-medium text-primary-600 d-block">
                                  Updated By
                                </label>
                                <p className="mb-0">{staffData.updated_by}</p>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                      {staffData.updated_datetime &&
                        staffData.updated_datetime !== "-" && (
                          <div className="col-lg-6 col-md-6">
                            <div className="p-2">
                              <div className="d-flex align-items-start">
                                <Icon
                                  icon="material-symbols:update"
                                  className="text-info-600 me-3 mt-1"
                                  style={{ fontSize: "18px", minWidth: "18px" }}
                                />
                                <div className="flex-grow-1">
                                  <label className="fw-medium text-primary-600 d-block">
                                    Last Updated
                                  </label>
                                  <p className="mb-0">
                                    {staffData.updated_datetime}
                                  </p>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </>
        ) : (
          <div className="text-center py-5">
            <div
              className="card shadow-sm"
              style={{ border: "none", borderRadius: "8px" }}
            >
              <div className="card-body py-5">
                <div className="p-4 rounded-circle bg-light d-inline-flex align-items-center justify-content-center mb-3">
                  <Icon
                    icon="material-symbols:error"
                    style={{ fontSize: "48px" }}
                    className="text-muted"
                  />
                </div>
                <h5 className="fw-medium text-primary-600 mb-2">
                  No Staff Data Found
                </h5>
                <p className="text-muted">
                  The requested staff information could not be retrieved. Please
                  verify the details and try again.
                </p>
                <button
                  className="btn btn-primary btn-sm mt-3"
                  onClick={() => window.location.reload(true)}
                >
                  Reload
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default StaffDetailpage;
